// Google Analytics 4 utility functions
// Optimized for performance with type safety

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: Record<string, unknown>[];
  }
}

export const GA_MEASUREMENT_ID = "G-CKLMPWXVZQ";

/**
 * Initialize Google Analytics
 * This is called automatically when the script loads
 */
export const initGA = (): void => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("js", new Date());

    // Enable debug mode in development
    const isDebugMode =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.search.includes("ga_debug=true");

    window.gtag("config", GA_MEASUREMENT_ID, {
      send_page_view: false, // We'll send page views manually for SPA
      debug_mode: isDebugMode, // Enable DebugView in GA4 for dev/testing
    });

    console.log("📊 Google Analytics initialized:", GA_MEASUREMENT_ID);
    console.log(
      "📊 Debug mode:",
      isDebugMode ? "ENABLED (events visible in DebugView)" : "DISABLED",
    );
  } else {
    console.warn("⚠️ Google Analytics not loaded - gtag not available");
  }
};

/**
 * Track page views
 * @param path - The page path
 * @param title - The page title
 */
export const trackPageView = (path: string, title?: string): void => {
  if (typeof window !== "undefined" && window.gtag) {
    const eventData = {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href,
    };
    window.gtag("event", "page_view", eventData);
    console.log("📊 GA Event: page_view", eventData);
  }
};

/**
 * Track custom events
 * @param eventName - The name of the event
 * @param eventParams - Additional parameters for the event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, string | number | boolean>,
): void => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, eventParams);
    console.log(`📊 GA Event: ${eventName}`, eventParams || {});
    console.log(`✅ Event sent to GA4`);
  } else {
    console.error(
      `❌ GA Event NOT sent - gtag not available:`,
      eventName,
      eventParams,
    );
  }
};

// Specific event tracking functions for better type safety and consistency

/**
 * Track when user clicks a call button
 */
export const trackCallClick = (
  location: "montreal" | "delson",
  phoneNumber: string,
): void => {
  trackEvent("call_click", {
    event_category: "engagement",
    event_label: location,
    phone_number: phoneNumber,
    location: location,
  });
};

/**
 * Track when user views location details
 */
export const trackLocationView = (location: "montreal" | "delson"): void => {
  trackEvent("location_view", {
    event_category: "engagement",
    event_label: location,
    location: location,
  });
};

/**
 * Track menu section views
 */
export const trackMenuView = (
  location: "montreal" | "delson",
  section: string,
): void => {
  trackEvent("menu_view", {
    event_category: "engagement",
    event_label: `${location}_${section}`,
    location: location,
    menu_section: section,
  });
};

/**
 * Track reservation button clicks
 */
export const trackReservationClick = (
  location: "montreal" | "delson",
): void => {
  trackEvent("reservation_click", {
    event_category: "conversion",
    event_label: location,
    location: location,
  });
};

/**
 * Track email clicks
 */
export const trackEmailClick = (source: string): void => {
  trackEvent("email_click", {
    event_category: "engagement",
    event_label: source,
    source: source,
  });
};

/**
 * Track order initiation
 */
export const trackOrderInitiation = (location: "montreal" | "delson"): void => {
  trackEvent("begin_checkout", {
    event_category: "conversion",
    event_label: location,
    location: location,
  });
};

/**
 * Track external link clicks
 */
export const trackExternalLink = (url: string, linkText: string): void => {
  trackEvent("external_link_click", {
    event_category: "engagement",
    event_label: linkText,
    link_url: url,
  });
};

/**
 * Track social media clicks
 */
export const trackSocialClick = (platform: string): void => {
  trackEvent("social_click", {
    event_category: "engagement",
    event_label: platform,
    platform: platform,
  });
};

/**
 * Track language change
 */
export const trackLanguageChange = (newLanguage: "en" | "fr"): void => {
  trackEvent("language_change", {
    event_category: "engagement",
    event_label: newLanguage,
    language: newLanguage,
  });
};
